import React, { useEffect, useMemo } from 'react';
import { Drawer, Form, Localization, usePostMessageContext } from 'connex-cds';
import { cloneDeep, find, get } from 'lodash';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { DataStore } from './datastore/DataStore';
import FormSubmit from './FormSubmit';
import { useFormConfig } from './get-form-config';
import {
  useCompanySetup,
  useMaterialComplianceSetup,
  useProductSetup,
  useVehicleSetup,
  useVehicleTypeSetup,
} from './MasterDataProvider';
import { MobileTicketErrorBoundary } from './MobileTicketErrorBoundary';
import { RefreshMonitor } from './RefreshMonitor';
import { TicketContextProvider, useTicketContext } from './TicketContext';
import { useInitializeMasterData } from './useInitializeMasterData';
import { useMonitorTimeZone } from './useMonitorTimeZone';
import { Wizard } from './wizard/Wizard';
import {
  buildFormattedMandatoryItems,
  buildMandatoryLineItems,
  buildMandatoryWaterItem,
  buildSetupMandatoryItems,
  isMasterDataReady,
  isVehicleTypeResolved,
} from './utils/mandatoryInitialValues';
import { getCachedItem } from '../../../../util/cache';
import { QrSummaryProvider } from './delivery-information/QrSummaryProvider';

const { FormProvider } = Form;
const { DrawerProvider } = Drawer;

const configTranslationLabelName = {
  getStringId: x => `SETUP_CONCRETE-CLASSES_${x}-NAME`.replace(/\s/gi, '_').toUpperCase(),
  getPath: 'id',
  setPath: 'labelName',
  defaultMessagePath: 'name',
};

const configTranslationLabelDescription = {
  getStringId: x => `SETUP_CONCRETE-CLASSES_${x}-DESCRIPTION`.replace(/\s/gi, '_').toUpperCase(),
  getPath: 'id',
  setPath: 'labelDescription',
  defaultMessagePath: 'description',
};

const filterCompliance = type => compliance => compliance?.classType === type;

const MobileTicket = () => {
  const navigate = useNavigate();
  const { ticket, setFinalized } = useTicketContext();
  const { translateObjects } = Localization.useTranslateObjects();
  const materialComplianceSetup = useMaterialComplianceSetup();
  const companySetup = useCompanySetup();
  const productSetup = useProductSetup();
  const vehicleSetup = useVehicleSetup();
  const vehicleTypeSetup = useVehicleTypeSetup();

  const loadedQuantity = useMemo(() => {
    const primaryLineItem = find(ticket?.lineItems, { isPrimary: true });

    if (primaryLineItem.isSplitLoad) {
      const loadedQtyValue = ticket?.lineItems?.reduce((acc, item) => acc + item?.quantity.value, 0);
      return { value: loadedQtyValue, uomCode: primaryLineItem.quantity.uomCode };
    }

    return primaryLineItem?.quantity;
  }, [ticket]);

  const activeMaterialCompliance = useMemo(() => {
    if (materialComplianceSetup?.isSuccess && materialComplianceSetup?.data) {
      return materialComplianceSetup?.data?.filter?.(compliance => compliance.status === 'ACTIVE');
    }

    return [];
  }, [materialComplianceSetup?.isSuccess, materialComplianceSetup?.data]);

  const airCompliance = useMemo(() => {
    const materialComplianceNew = activeMaterialCompliance?.filter?.(filterCompliance('air'));
    const materialComplianceOld = activeMaterialCompliance?.filter?.(filterCompliance('AIR'));
    const materialCompliance = [...materialComplianceNew, ...materialComplianceOld];

    if (materialCompliance?.length) {
      const materialComplianceName = translateObjects(materialCompliance, configTranslationLabelName);
      const materialComplianceDescription = translateObjects(materialComplianceName, configTranslationLabelDescription);

      return materialComplianceDescription;
    }

    return [];
  }, [activeMaterialCompliance, translateObjects]);

  const consistenceCompliance = useMemo(() => {
    const materialComplianceNew = activeMaterialCompliance?.filter?.(filterCompliance('consistence'));
    const materialComplianceOld = activeMaterialCompliance?.filter?.(filterCompliance('CONSISTENCE'));
    const materialCompliance = [...materialComplianceNew, ...materialComplianceOld];

    if (materialCompliance?.length) {
      const materialComplianceName = translateObjects(materialCompliance, configTranslationLabelName);
      const materialComplianceDescription = translateObjects(materialComplianceName, configTranslationLabelDescription);

      return materialComplianceDescription;
    }

    return [];
  }, [activeMaterialCompliance, translateObjects]);

  const airTempCompliance = useMemo(() => {
    const materialComplianceNew = activeMaterialCompliance?.filter?.(filterCompliance('airTemperature'));
    const materialComplianceOld = activeMaterialCompliance?.filter?.(filterCompliance('AIR_TEMP'));
    const materialCompliance = [...materialComplianceNew, ...materialComplianceOld];

    if (materialCompliance?.length) {
      const materialComplianceName = translateObjects(materialCompliance, configTranslationLabelName);
      const materialComplianceDescription = translateObjects(materialComplianceName, configTranslationLabelDescription);

      return materialComplianceDescription;
    }

    return [];
  }, [activeMaterialCompliance, translateObjects]);

  const concreteTempCompliance = useMemo(() => {
    const materialComplianceNew = activeMaterialCompliance?.filter?.(filterCompliance('temperature'));
    const materialComplianceOld = activeMaterialCompliance?.filter?.(filterCompliance('CONCRETE_TEMP'));
    const materialCompliance = [...materialComplianceNew, ...materialComplianceOld];

    if (materialCompliance?.length) {
      const materialComplianceName = translateObjects(materialCompliance, configTranslationLabelName);
      const materialComplianceDescription = translateObjects(materialComplianceName, configTranslationLabelDescription);

      return materialComplianceDescription;
    }

    return [];
  }, [activeMaterialCompliance, translateObjects]);

  const cylindersCompliance = useMemo(() => {
    const materialComplianceNew = activeMaterialCompliance?.filter?.(filterCompliance('cylinders'));
    const materialComplianceOld = activeMaterialCompliance?.filter?.(filterCompliance('CYLINDERS'));
    const materialCompliance = [...materialComplianceNew, ...materialComplianceOld];

    if (materialCompliance?.length) {
      const materialComplianceName = translateObjects(materialCompliance, configTranslationLabelName);
      const materialComplianceDescription = translateObjects(materialComplianceName, configTranslationLabelDescription);

      return materialComplianceDescription;
    }

    return [];
  }, [activeMaterialCompliance, translateObjects]);

  const primaryLineItem = useMemo(() => find(ticket?.lineItems, { isPrimary: true }), [ticket?.lineItems]);

  const formConfig = useFormConfig(
    loadedQuantity,
    ticket?.vehicleId,
    airCompliance?.[0],
    consistenceCompliance?.[0],
    airTempCompliance?.[0],
    concreteTempCompliance?.[0],
    cylindersCompliance?.[0],
    primaryLineItem
  );

  const vehicleTypeData = useMemo(() => {
    const targetVehicle = find(vehicleSetup?.data, { id: ticket?.vehicleId });
    return find(vehicleTypeSetup?.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
  }, [vehicleSetup?.data, vehicleTypeSetup?.data, ticket?.vehicleId]);

  const vehicleType = useMemo(() => {
    const targetVehicle = find(vehicleSetup?.data, { id: ticket?.vehicleId });
    const targetVehicleType = find(vehicleTypeSetup?.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
    return targetVehicleType?.id;
  }, [vehicleSetup?.data, vehicleTypeSetup?.data, ticket?.vehicleId]);

  const vehicleTypeResolved = useMemo(
    () =>
      isVehicleTypeResolved({
        vehicleSetupLoaded: vehicleSetup?.isSuccess,
        vehicleTypeSetupLoaded: vehicleTypeSetup?.isSuccess,
        vehicles: vehicleSetup?.data,
        vehicleTypes: vehicleTypeSetup?.data,
        vehicleId: ticket?.vehicleId,
      }),
    [
      vehicleSetup?.isSuccess,
      vehicleSetup?.data,
      vehicleTypeSetup?.isSuccess,
      vehicleTypeSetup?.data,
      ticket?.vehicleId,
    ]
  );

  const masterDataReady = useMemo(
    () =>
      isMasterDataReady({
        companySetupLoaded: companySetup?.isSuccess,
        productSetupLoaded: productSetup?.isSuccess,
        vehicleSetupLoaded: vehicleSetup?.isSuccess,
        vehicleTypeSetupLoaded: vehicleTypeSetup?.isSuccess,
        vehicleTypeResolved,
      }),
    [
      companySetup?.isSuccess,
      productSetup?.isSuccess,
      vehicleSetup?.isSuccess,
      vehicleTypeSetup?.isSuccess,
      vehicleTypeResolved,
    ]
  );

  const mandatoryWaterItem = useMemo(
    () =>
      buildMandatoryWaterItem({
        companySetup: companySetup?.data,
        products: productSetup?.data,
        vehicleTypeData,
        vehicleTypeResolved,
      }),
    [companySetup?.data, productSetup?.data, vehicleTypeData, vehicleTypeResolved]
  );

  const setupMandatoryItems = useMemo(
    () =>
      buildSetupMandatoryItems({
        products: productSetup?.data,
        companySetup: companySetup?.data,
        ticketOriginCountryCode: ticket?.origin?.address?.countryCode,
        vehicleType,
      }),
    [companySetup?.data, productSetup?.data, ticket?.origin?.address?.countryCode, vehicleType]
  );

  const formattedMandatoryItems = useMemo(
    () => buildFormattedMandatoryItems(setupMandatoryItems),
    [setupMandatoryItems]
  );

  const initialValues = useMemo(() => {
    const state = getCachedItem('state');
    if (state) {
      const stateObj = JSON.parse(state);
      if (stateObj[ticket.crn]) {
        if (stateObj[ticket.crn]?.signature?.image?.length > 0) {
          setFinalized(true);
        }

        return stateObj[ticket.crn];
      }
    }

    if (!masterDataReady) {
      return;
    }

    const cloned = cloneDeep(ticket);

    const mandatoryItems = buildMandatoryLineItems({
      ticketLineItems: cloned?.lineItems,
      formattedMandatoryItems,
      companySetup: companySetup?.data,
      vehicleTypeData,
      vehicleTypeResolved,
    });

    const waterAddedEvents = [];
    const isAccepted = cloned?.customerStatus === 'ACCEPTED' || cloned?.customerStatus?.statusCode === 'ACCEPTED';
    if (mandatoryWaterItem && !isAccepted) {
      waterAddedEvents.push(mandatoryWaterItem);
    }
    if (cloned?.waterAddedEvents) {
      waterAddedEvents.push(...cloned?.waterAddedEvents);
    }

    const signatureGetter = signatureType => {
      const signature = get(cloned, `signatures.${signatureType}`);
      if (signature?.link) {
        return {
          formattedTimestamp: signature?.formattedTimestamp,
          image: signature?.link,
          signerName: signature?.signerName,
          timestamp: signature?.timestamp,
        };
      }

      return null;
    };

    const signature = signatureGetter('customer-acceptance');
    const waterReleaseSignature = signatureGetter('water-release-acceptance');
    const damageWaiverSignature = signatureGetter('damage-waiver-acceptance');
    const dynamicWaiverSignature = signatureGetter('dynamic-waiver-acceptance');
    const producerSignature = signatureGetter('producer-acceptance');
    const inspectorSignature = signatureGetter('inspector-acceptance');

    const returnedMaterialGetter = () => {
      const [returnedItem] =
        cloned?.lineItems?.filter(item => !!item?.returnedConcreteReason && !!item?.returnedQuantity) ?? [];

      if (!returnedItem) {
        return null;
      }

      return {
        returnConcreteOnTruck: {
          value: returnedItem?.returnedQuantity?.value,
          uomCode: returnedItem?.returnedQuantity?.uomCode,
        },
        returnReason: {
          id: returnedItem?.returnedConcreteReason?.id,
          reasonRef: returnedItem?.returnedConcreteReason?.reasonRef,
          description: returnedItem?.returnedConcreteReason?.description,
        },
        returnConcreteSource: {
          id: returnedItem?.returnedConcreteReason?.source,
        },
      };
    };

    const returnedMaterial = returnedMaterialGetter();

    return {
      lineItems: mandatoryItems?.length
        ? cloned?.lineItems
            ?.filter?.(item => !item?.isPrimary)
            ?.filter?.(item => !item?.isSplitLoad)
            ?.concat?.(mandatoryItems)
        : cloned?.lineItems?.filter?.(item => !item?.isPrimary)?.filter?.(item => !item?.isSplitLoad),
      waterAddedEvents,
      ticketRef: cloned?.crn,
      ticketEvents: cloned?.ticketEvents,
      unloadingMethod: cloned?.unloadingMethod,
      ...(signature ? { signature } : {}),
      ...(waterReleaseSignature ? { waterReleaseSignature } : {}),
      ...(damageWaiverSignature ? { damageWaiverSignature } : {}),
      ...(dynamicWaiverSignature ? { dynamicWaiverSignature } : {}),
      ...(producerSignature ? { producerSignature } : {}),
      ...(inspectorSignature ? { inspectorSignature } : {}),
      ...(returnedMaterial ? { ...returnedMaterial } : {}),
    };
  }, [
    companySetup?.data,
    formattedMandatoryItems,
    mandatoryWaterItem,
    masterDataReady,
    setFinalized,
    ticket,
    vehicleTypeData,
    vehicleTypeResolved,
  ]);

  useEffect(() => {
    navigate('ticketDetail');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!initialValues) {
    return null;
  }

  return (
    <FormProvider key={ticket?.crn} config={formConfig} initialValues={initialValues}>
      <QrSummaryProvider>
        <RefreshMonitor>
          <DrawerProvider>
            <FormSubmit>
              <Routes>
                <Route path=":stepId" element={<Wizard />} />
              </Routes>
            </FormSubmit>
          </DrawerProvider>
        </RefreshMonitor>
      </QrSummaryProvider>
    </FormProvider>
  );
};

const Styled = styled.div`
  height: 100%;

  @media (max-width: 950px) {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

export default ({ driverExperience, selectedTicket = false, activeTicket = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendMessage } = usePostMessageContext();
  useInitializeMasterData();
  useMonitorTimeZone();

  useEffect(() => {
    if (activeTicket) {
      if (!location?.state?.crn) navigate('../');
    }
    if (selectedTicket) {
      if (!location?.state?.crn) navigate('../');
    }
  }, [activeTicket, selectedTicket]);

  useEffect(() => {
    sendMessage(JSON.stringify({ loaded: true }));
    sendMessage(JSON.stringify({ type: 'debug', message: 'Mobile Ticket Loaded' }));
  }, [sendMessage]);

  return (
    <MobileTicketErrorBoundary>
      <Styled>
        <DataStore>
          <TicketContextProvider
            driverExperience={driverExperience}
            selectedTicket={selectedTicket}
            activeTicket={activeTicket}
          >
            <MobileTicket />
          </TicketContextProvider>
        </DataStore>
      </Styled>
    </MobileTicketErrorBoundary>
  );
};
